import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import {
  Brain,
  AlertTriangle,
  TrendingUp,
  Settings,
  Save,
  Cpu,
  HardDrive,
  Network,
  Activity,
  Zap,
  LayoutDashboard,
  Lightbulb,
} from "lucide-react";

const STORAGE_KEY = "netmonitor_ai_config";

export type AIConfig = {
  general: {
    mlMode: "openai" | "local";
  };
  dashboard: {
    refetchIntervalSeconds: number;
    anomalySummaryLimit: number;
    predictionSummaryLimit: number;
    nlpSearchEnabled: boolean;
  };
  insights: {
    refreshCooldownSeconds: number;
    defaultAnomalyLimit: number;
    defaultPredictionLimit: number;
  };
  anomaly: {
    summaryLimit: number;
    defaultSeverityFilter: string;
    sensitivity: number;
    rules: {
      cpuUsageSpike: boolean;
      memoryLeakDetection: boolean;
      networkLatency: boolean;
      connectionAnomalies: boolean;
    };
  };
  predictive: {
    summaryLimit: number;
    defaultMetricFilter: string;
    timeHorizonHours: number;
    models: {
      cpu: boolean;
      memory: boolean;
      network: boolean;
      disk: boolean;
    };
  };
};

const defaultConfig: AIConfig = {
  general: {
    mlMode: "openai",
  },
  dashboard: {
    refetchIntervalSeconds: 30,
    anomalySummaryLimit: 10,
    predictionSummaryLimit: 10,
    nlpSearchEnabled: true,
  },
  insights: {
    refreshCooldownSeconds: 30,
    defaultAnomalyLimit: 10,
    defaultPredictionLimit: 10,
  },
  anomaly: {
    summaryLimit: 10,
    defaultSeverityFilter: "all",
    sensitivity: 0.1,
    rules: {
      cpuUsageSpike: true,
      memoryLeakDetection: true,
      networkLatency: true,
      connectionAnomalies: true,
    },
  },
  predictive: {
    summaryLimit: 10,
    defaultMetricFilter: "all",
    timeHorizonHours: 24,
    models: {
      cpu: true,
      memory: true,
      network: true,
      disk: true,
    },
  },
};

function loadConfig(): AIConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AIConfig>;
      return {
        general: { ...defaultConfig.general, ...parsed.general },
        dashboard: { ...defaultConfig.dashboard, ...parsed.dashboard },
        insights: { ...defaultConfig.insights, ...parsed.insights },
        anomaly: {
          ...defaultConfig.anomaly,
          ...parsed.anomaly,
          rules: { ...defaultConfig.anomaly.rules, ...parsed.anomaly?.rules },
        },
        predictive: {
          ...defaultConfig.predictive,
          ...parsed.predictive,
          models: { ...defaultConfig.predictive.models, ...parsed.predictive?.models },
        },
      };
    }
  } catch {
    // ignore
  }
  return defaultConfig;
}

function saveConfig(config: AIConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export function getAIConfig(): AIConfig {
  return loadConfig();
}

export default function AIConfiguration() {
  const { toast } = useToast();
  const [config, setConfig] = useState<AIConfig>(defaultConfig);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setConfig(loadConfig());
  }, []);

  const update = <K extends keyof AIConfig>(
    section: K,
    field: keyof AIConfig[K],
    value: unknown
  ) => {
    setConfig((prev) => {
      const sec = prev[section] as Record<string, unknown>;
      if (typeof sec === "object" && sec !== null && !Array.isArray(sec)) {
        return {
          ...prev,
          [section]: { ...sec, [field]: value },
        };
      }
      return prev;
    });
    setSaved(false);
  };

  const updateNested = (
    section: "anomaly" | "predictive",
    nested: "rules" | "models",
    key: string,
    value: boolean
  ) => {
    setConfig((prev) => {
      const sec = prev[section];
      const nestedObj = nested === "rules"
        ? (sec as AIConfig["anomaly"]).rules
        : (sec as AIConfig["predictive"]).models;
      return {
        ...prev,
        [section]: {
          ...sec,
          [nested]: { ...nestedObj, [key]: value },
        },
      };
    });
    setSaved(false);
  };

  const handleSave = () => {
    saveConfig(config);
    setSaved(true);
    toast({
      title: "AI configuration saved",
      description:
        "Settings saved. AI Dashboard, Insights, Anomaly Detection, and Predictive Analytics will use these options.",
    });
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-dark flex items-center">
          <Brain className="h-6 w-6 text-violet-500 mr-3" />
          AI Configuration
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure anomaly detection and predictive analytics options used across AI pages
        </p>
      </div>

      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/settings"
          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          <Settings className="h-4 w-4" />
          API key and model are configured in Settings
        </Link>
        <Button onClick={handleSave} disabled={saved}>
          <Save className="h-4 w-4 mr-2" />
          {saved ? "Saved" : "Save configuration"}
        </Button>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 w-full max-w-2xl">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="dashboard">AI Dashboard</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
          <TabsTrigger value="anomaly">Anomaly Detection</TabsTrigger>
          <TabsTrigger value="predictive">Predictive Analytics</TabsTrigger>
        </TabsList>

        {/* General */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General AI Settings</CardTitle>
              <CardDescription>
                Shared options for anomaly detection and predictive analytics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>ML Mode</Label>
                <select
                  className="flex h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={config.general.mlMode}
                  onChange={(e) =>
                    update("general", "mlMode", e.target.value as "openai" | "local")
                  }
                >
                  <option value="openai">OpenAI</option>
                  <option value="local">Local</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  OpenAI uses cloud models; Local uses on-device inference when available.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Dashboard — from ai-dashboard.tsx */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LayoutDashboard className="h-5 w-5 text-blue-500" />
                AI Dashboard
              </CardTitle>
              <CardDescription>
                Options for the AI Dashboard page: refetch interval, summary limits, and NLP search
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Refetch interval (seconds)</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.dashboard.refetchIntervalSeconds}
                    onChange={(e) =>
                      update("dashboard", "refetchIntervalSeconds", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={15}>15</option>
                    <option value={30}>30</option>
                    <option value={60}>60</option>
                    <option value={120}>120</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    How often the dashboard refreshes predictions and anomalies
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Anomaly summary limit</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.dashboard.anomalySummaryLimit}
                    onChange={(e) =>
                      update("dashboard", "anomalySummaryLimit", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Number of anomalies shown on the dashboard
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Prediction summary limit</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.dashboard.predictionSummaryLimit}
                    onChange={(e) =>
                      update("dashboard", "predictionSummaryLimit", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Number of predictions shown on the dashboard
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <span className="text-sm font-medium flex items-center gap-2">
                  <Brain className="h-4 w-4" /> Natural language search
                </span>
                <Switch
                  checked={config.dashboard.nlpSearchEnabled}
                  onCheckedChange={(v) => update("dashboard", "nlpSearchEnabled", v)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enable or disable the NLP search box on the AI Dashboard
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights — from ai-insights.tsx */}
        <TabsContent value="insights">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                AI Insights
              </CardTitle>
              <CardDescription>
                Options for the AI Insights page: refresh cooldown and default list limits
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Refresh cooldown (seconds)</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.insights.refreshCooldownSeconds}
                    onChange={(e) =>
                      update("insights", "refreshCooldownSeconds", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={15}>15</option>
                    <option value={30}>30</option>
                    <option value={60}>60</option>
                    <option value={120}>120</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Minimum time between manual refreshes on AI Insights
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Default anomaly list limit</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.insights.defaultAnomalyLimit}
                    onChange={(e) =>
                      update("insights", "defaultAnomalyLimit", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={40}>40</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Initial number of anomalies to show in AI Insights
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Default prediction list limit</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.insights.defaultPredictionLimit}
                    onChange={(e) =>
                      update("insights", "defaultPredictionLimit", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={40}>40</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Initial number of predictions to show in AI Insights
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Anomaly Detection — from anomaly-detection.tsx */}
        <TabsContent value="anomaly">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Anomaly Detection
              </CardTitle>
              <CardDescription>
                Options for the Anomaly Detection page: limits, filters, sensitivity, and detection rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Anomaly summary limit</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.anomaly.summaryLimit}
                    onChange={(e) =>
                      update("anomaly", "summaryLimit", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Number of recent anomalies to load in the summary
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Default severity filter</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.anomaly.defaultSeverityFilter}
                    onChange={(e) =>
                      update("anomaly", "defaultSeverityFilter", e.target.value)
                    }
                  >
                    <option value="all">All Severities</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Initial filter for anomaly list (All, High, Medium, Low)
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Detection sensitivity: {config.anomaly.sensitivity}</Label>
                <Slider
                  value={[config.anomaly.sensitivity]}
                  onValueChange={([v]) => update("anomaly", "sensitivity", v)}
                  min={0.01}
                  max={1}
                  step={0.01}
                />
                <p className="text-xs text-muted-foreground">
                  Lower = fewer, higher-confidence anomalies; higher = more alerts, may include noise
                </p>
              </div>

              <div className="space-y-3">
                <Label>Detection rules</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Enable or disable each anomaly detection rule
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Cpu className="h-4 w-4" /> CPU Usage Spike
                    </span>
                    <Switch
                      checked={config.anomaly.rules.cpuUsageSpike}
                      onCheckedChange={(v) =>
                        updateNested("anomaly", "rules", "cpuUsageSpike", v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4" /> Memory Leak Detection
                    </span>
                    <Switch
                      checked={config.anomaly.rules.memoryLeakDetection}
                      onCheckedChange={(v) =>
                        updateNested("anomaly", "rules", "memoryLeakDetection", v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Network className="h-4 w-4" /> Network Latency
                    </span>
                    <Switch
                      checked={config.anomaly.rules.networkLatency}
                      onCheckedChange={(v) =>
                        updateNested("anomaly", "rules", "networkLatency", v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Zap className="h-4 w-4" /> Connection Anomalies
                    </span>
                    <Switch
                      checked={config.anomaly.rules.connectionAnomalies}
                      onCheckedChange={(v) =>
                        updateNested("anomaly", "rules", "connectionAnomalies", v)
                      }
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Predictive Analytics — from predictive-analytics.tsx */}
        <TabsContent value="predictive">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                Predictive Analytics
              </CardTitle>
              <CardDescription>
                Options for the Predictive Analytics page: limits, filters, time horizon, and models
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Prediction summary limit</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.predictive.summaryLimit}
                    onChange={(e) =>
                      update("predictive", "summaryLimit", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Number of recent predictions to load
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Default metric filter</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.predictive.defaultMetricFilter}
                    onChange={(e) =>
                      update("predictive", "defaultMetricFilter", e.target.value)
                    }
                  >
                    <option value="all">All Metrics</option>
                    <option value="CPU Usage">CPU Usage</option>
                    <option value="Memory Usage">Memory Usage</option>
                    <option value="Disk Usage">Disk Usage</option>
                    <option value="Network Traffic">Network Traffic</option>
                    <option value="Response Time">Response Time</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Initial filter for predictions list
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Time horizon (hours ahead)</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={config.predictive.timeHorizonHours}
                    onChange={(e) =>
                      update("predictive", "timeHorizonHours", parseInt(e.target.value, 10))
                    }
                  >
                    <option value={6}>6 hours</option>
                    <option value={12}>12 hours</option>
                    <option value={24}>24 hours</option>
                    <option value={48}>48 hours</option>
                    <option value={72}>72 hours</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    How far ahead to predict (e.g. CPU, memory)
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Prediction models</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Enable or disable each prediction model
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Cpu className="h-4 w-4" /> CPU Prediction Model
                    </span>
                    <Switch
                      checked={config.predictive.models.cpu}
                      onCheckedChange={(v) =>
                        updateNested("predictive", "models", "cpu", v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Activity className="h-4 w-4" /> Memory Prediction Model
                    </span>
                    <Switch
                      checked={config.predictive.models.memory}
                      onCheckedChange={(v) =>
                        updateNested("predictive", "models", "memory", v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <Network className="h-4 w-4" /> Network Prediction Model
                    </span>
                    <Switch
                      checked={config.predictive.models.network}
                      onCheckedChange={(v) =>
                        updateNested("predictive", "models", "network", v)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <span className="text-sm font-medium flex items-center gap-2">
                      <HardDrive className="h-4 w-4" /> Disk Prediction Model
                    </span>
                    <Switch
                      checked={config.predictive.models.disk}
                      onCheckedChange={(v) =>
                        updateNested("predictive", "models", "disk", v)
                      }
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
